import { EvalSample, LogUpdate } from "@tsmono/inspect-common/types";
import { encodePathParts } from "@tsmono/util";

import { sampleIdsEqual } from "../../app/shared/sample";
import { WorkResult } from "../../utils/workQueue";
import {
  openRemoteLogFile,
  RemoteLogFile,
  SampleNotFoundError,
} from "../remote/remoteLogFile";
import { DirectFetchError } from "../remote/remotePendingSampleData";
import { FileSizeLimitError } from "../remote/remoteZipFile";

import {
  ClientAPI,
  EditLogResult,
  LogContents,
  LogDetails,
  LogPreview,
  LogViewAPI,
  PendingSampleResponse,
  ProgressCallback,
  SampleDataResponse,
  SampleRef,
  SampleSummary,
} from "./types";

const isEvalFile = (file: string) => {
  return file.endsWith(".eval");
};

/**
 * Represents an error thrown when a file exceeds the maximum allowed size.
 */
export class SampleSizeLimitedExceededError extends Error {
  readonly id: string | number;
  readonly epoch: number;
  readonly maxBytes: number;
  readonly displayStack: boolean;

  constructor(id: string | number, epoch: number, maxBytes: number) {
    super(
      `Sample ${id} in epoch ${epoch} exceeds the maximum supported size (${maxBytes / 1024 / 1024}MB) and cannot be loaded.`
    );

    this.name = "SampleSizeLimitedExceededError";
    this.id = id;
    this.epoch = epoch;
    this.maxBytes = maxBytes;
    this.displayStack = false;

    Object.setPrototypeOf(this, SampleSizeLimitedExceededError.prototype);
  }
}

interface LoadedLogFile {
  file?: string;
  remoteLog?: RemoteLogFile;
}

/**
 * Adapt a backend `LogViewAPI` into the `ClientAPI` the app consumes:
 * format handling (.eval zip vs JSON), per-log caching, sample-data path
 * selection, and edit etag plumbing. Dir-agnostic — the dir binding lives
 * in the backend instance (see the contract on `LogViewAPI`).
 */
export const clientApi = (api: LogViewAPI, debug = false): ClientAPI => {
  let current_log: LogContents | undefined = undefined;
  let current_path: string | undefined = undefined;
  const pendingLogs = new Map<string, Promise<LogContents>>();

  const loadedEvalFile: LoadedLogFile = {
    file: undefined,
    remoteLog: undefined,
  };

  const remoteEvalFile = async (log_file: string, cached: boolean = false) => {
    if (cached && loadedEvalFile.file === log_file) {
      return loadedEvalFile.remoteLog;
    }

    const remoteLog = await openRemoteLogFile(
      api,
      encodePathParts(log_file),
      5
    );

    if (cached) {
      loadedEvalFile.file = log_file;
      loadedEvalFile.remoteLog = remoteLog;
    }

    return remoteLog;
  };

  /**
   * Gets a log
   */
  const get_log = async (
    log_file: string,
    cached = false
  ): Promise<LogContents> => {
    // If the requested log is different or no cached log exists, start fetching
    if (!cached || log_file !== current_path || !current_log) {
      // Different logs can load concurrently during navigation and backfill.
      const pending = pendingLogs.get(log_file);
      if (pending) return pending;

      // Otherwise, create a new promise for fetching the log
      const request = api
        .get_log_contents(log_file, 100)
        .then((log) => {
          current_log = log;
          current_path = log_file;
          return log;
        })
        .finally(() => {
          pendingLogs.delete(log_file);
        });
      pendingLogs.set(log_file, request);
      return request;
    }
    return current_log;
  };

  /**
   * Gets a log summary
   */
  const get_log_details = async (
    log_file: string,
    cached = true
  ): Promise<LogDetails> => {
    if (isEvalFile(log_file)) {
      const remoteLogFile = await remoteEvalFile(log_file, cached);
      if (remoteLogFile) {
        const details = await remoteLogFile.readLogSummary();
        // A running log's zip is still being appended, so its memoized
        // snapshot (the central directory at open time) can never see later
        // flushes — drop it so every read while running re-opens fresh.
        // Completed logs stay memoized.
        if (details.status === "started" && loadedEvalFile.file === log_file) {
          loadedEvalFile.file = undefined;
          loadedEvalFile.remoteLog = undefined;
        }
        return details;
      } else {
        throw new Error("Unable to read remote eval file");
      }
    } else {
      const logContents = await get_log(log_file);
      // Samples in a parsed JSON log are already normalized (#555), so this
      // projection genuinely satisfies SampleSummary.
      const sampleSummaries: SampleSummary[] = logContents.parsed.samples
        ? logContents.parsed.samples.map((sample) => {
            return {
              id: sample.id,
              epoch: sample.epoch,
              uuid: sample.uuid,
              input: sample.input,
              target: sample.target,
              scores: sample.scores,
              metadata: sample.metadata,
              error: sample.error?.message,
              // The summary's limit is the flattened form of the sample's
              // limit object (mirrors Python's EvalSample.summary()).
              limit: sample.limit?.type,
              limit_reason: sample.limit?.reason,
              retries: sample.error_retries?.length,
              // A sample serialized into the log body is settled by
              // definition.
              completed: true,
              model_usage: sample.model_usage,
              role_usage: sample.role_usage,
              model_fallbacks: sample.model_fallbacks,
              started_at: sample.started_at,
              completed_at: sample.completed_at,
              total_time: sample.total_time,
              working_time: sample.working_time,
            };
          })
        : [];

      const parsed = logContents.parsed;
      return {
        version: parsed.version,
        status: parsed.status,
        eval: parsed.eval,
        plan: parsed.plan,
        results: parsed.results,
        stats: parsed.stats,
        error: parsed.error,
        tags: parsed.tags,
        metadata: parsed.metadata,
        log_updates: parsed.log_updates,
        config_updates: parsed.config_updates,
        sampleSummaries,
      };
    }
  };

  /**
   * Gets a sample
   */
  const get_log_sample = async (
    log_file: string,
    id: string | number,
    epoch: number,
    onProgress?: ProgressCallback
  ): Promise<EvalSample | undefined> => {
    if (isEvalFile(log_file)) {
      async function fetchSample(useCache: boolean) {
        const remoteLogFile = await remoteEvalFile(log_file, useCache);
        if (!remoteLogFile) {
          throw new Error(`Unable to read remote eval file ${log_file}`);
        }
        return await remoteLogFile.readSample(String(id), epoch, onProgress);
      }

      function handleError(error: unknown) {
        if (error instanceof SampleNotFoundError) return undefined;
        if (error instanceof FileSizeLimitError) {
          throw new SampleSizeLimitedExceededError(id, epoch, error.maxBytes);
        }
        throw error;
      }

      try {
        // First attempt with cache
        return await fetchSample(true);
      } catch (error) {
        if (error instanceof SampleNotFoundError) {
          try {
            // Retry without cache
            return await fetchSample(false);
          } catch (retryError) {
            return handleError(retryError);
          }
        } else {
          return handleError(error);
        }
      }
    } else {
      const logContents = await get_log(log_file, true);
      // const samples =
      if (logContents.parsed.samples && logContents.parsed.samples.length > 0) {
        return logContents.parsed.samples.find((sample) => {
          return sampleIdsEqual(sample.id, id) && sample.epoch === epoch;
        });
      }
    }
    return undefined;
  };

  const get_log_zip_access = async (log_file: string) => {
    if (!isEvalFile(log_file)) {
      return undefined;
    }
    const remoteLogFile = await remoteEvalFile(log_file, true);
    if (!remoteLogFile) {
      throw new Error(`Unable to read remote eval file ${log_file}`);
    }
    return remoteLogFile.zipAccess();
  };

  const read_eval_file_log_summary = async (log_file: string) => {
    // If the API supports this, delegate to it
    if (api.get_log_summary) {
      return api.get_log_summary(log_file);
    } else {
      // Don't re-use the eval log file since we know these are all different log files
      const remoteLogFile = await openRemoteLogFile(
        api,
        encodePathParts(log_file),
        5
      );
      return remoteLogFile.readEvalBasicInfo();
    }
  };

  const read_one_summary = async (log_file: string): Promise<LogPreview> => {
    if (isEvalFile(log_file)) {
      return read_eval_file_log_summary(log_file);
    }
    const summaries = await api.get_log_summaries([log_file]);
    const summary = summaries[0];
    if (!summary) {
      throw new Error(`No summary returned for ${log_file}`);
    }
    return summary;
  };

  // Batch first: reading .eval headers client-side costs ~5 HTTP round-trips
  // per file, so large log directories take minutes per-file.
  // TODO(better fix): /log-headers should return per-file success|error results
  // (server: fastapi_server.py api_log_headers + read_eval_log_headers_async).
  // Until then one unreadable file fails the whole batched request, so isolate
  // failures client-side by falling back to per-file reads, each caught.
  const get_log_summaries_settled = async (
    log_files: string[]
  ): Promise<WorkResult<LogPreview>[]> => {
    try {
      const summaries = await api.get_log_summaries(log_files);
      if (summaries.length === log_files.length) {
        return summaries.map((value) => ({ ok: true, value }));
      }
    } catch {
      // fall through to per-file reads
    }
    return Promise.all(
      log_files.map(async (file) => {
        try {
          return { ok: true as const, value: await read_one_summary(file) };
        } catch (e) {
          return {
            ok: false as const,
            error: e instanceof Error ? e : new Error(String(e)),
          };
        }
      })
    );
  };

  const get_log_pending_samples = (
    log_file: string,
    etag?: string
  ): Promise<PendingSampleResponse> => {
    if (!api.eval_pending_samples) {
      throw new Error("API doesn't support streamed samples");
    }
    return api.eval_pending_samples(log_file, etag);
  };

  // Probe-once selector: the only signal that flips the choice is an
  // explicit "not supported" return from the direct probe; real errors
  // bubble up.
  const sampleDataPathByLog = new Map<string, "direct" | "proxy">();

  // Per-log cache of the most recent ETag observed for each log. The
  // edit dialogs currently call `edit_log(file, update)` with no third
  // argument, and without this cache we'd silently throw away
  // `result.etag` — making the server's If-Match / 412 protection
  // unreachable from the shipped UI (only tests and external callers
  // would ever trigger it).
  //
  // The cache is fed from two places:
  //   - `get_log_details` (below): seeds the entry on log open from
  //     `LogDetails.etag`, which `openRemoteLogFile` lifts off the
  //     S3 head_object response via `get_log_info`. This is what
  //     protects the *first* edit in a session.
  //   - `edit_log` (below): updates the entry from `result.etag` so
  //     chained edits within a session keep the protection live.
  //
  // The middleware below re-supplies the cached etag whenever the
  // caller doesn't pass one explicitly.
  const editEtagByLog = new Map<string, string>();

  const get_log_sample_data = async (
    log_file: string,
    id: string | number,
    epoch: number,
    last_event?: number,
    last_attachment?: number,
    last_message_pool?: number,
    last_call_pool?: number
  ): Promise<SampleDataResponse | undefined> => {
    if (!api.eval_log_sample_data) {
      throw new Error("API doesn't supported streamed sample data");
    }

    const fetchViaProxy = () =>
      api.eval_log_sample_data!(
        log_file,
        id,
        epoch,
        last_event,
        last_attachment,
        last_message_pool,
        last_call_pool
      );

    // A presigned segment fetch that the browser couldn't complete (e.g. bucket
    // CORS on the viewer origin) means direct is unusable here even though the
    // view server can reach storage — pin proxy and stream same-origin instead.
    const fallBackToProxy = (e: unknown) => {
      if (!(e instanceof DirectFetchError)) {
        throw e;
      }
      sampleDataPathByLog.set(log_file, "proxy");
      return fetchViaProxy();
    };

    let path = sampleDataPathByLog.get(log_file);
    if (path === undefined && api.eval_log_sample_data_direct) {
      try {
        const probe = await api.eval_log_sample_data_direct(
          log_file,
          id,
          epoch,
          last_event,
          last_attachment,
          last_message_pool,
          last_call_pool
        );
        if (probe !== undefined) {
          sampleDataPathByLog.set(log_file, "direct");
          return probe;
        }
      } catch (e) {
        return fallBackToProxy(e);
      }
    }
    if (path === undefined) {
      path = "proxy";
      sampleDataPathByLog.set(log_file, path);
    }

    if (path === "direct") {
      try {
        const result = await api.eval_log_sample_data_direct!(
          log_file,
          id,
          epoch,
          last_event,
          last_attachment,
          last_message_pool,
          last_call_pool
        );
        if (result === undefined) {
          // Probe succeeded but a later call says "not supported" — server state
          // changed; fail loudly rather than silently switching paths.
          throw new Error(
            "Direct pending-sample-data path returned 'not supported' after probe"
          );
        }
        return result;
      } catch (e) {
        return fallBackToProxy(e);
      }
    }
    return fetchViaProxy();
  };

  const middleware = debug
    ? createMiddlewareWrapper([debugMiddleware])
    : <T extends AnyFn>(_name: string, fn: T): T => fn;

  return {
    client_events: middleware("client_events", () => {
      return api.client_events();
    }),
    get_logs: middleware(
      "get_log_files",
      (
        mtime: number,
        clientFileCount: number,
        clientNames?: readonly string[]
      ) => api.get_logs(mtime, clientFileCount, clientNames)
    ),
    get_eval_set: middleware("get_eval_set", (dir?: string) => {
      return api.get_eval_set(dir);
    }),
    get_flow: middleware("get_flow", (dir?: string) => {
      return api.get_flow(dir);
    }),
    get_log_summaries_settled: middleware(
      "get_log_summaries_settled",
      get_log_summaries_settled
    ),
    get_log_details: middleware(
      "get_log_details",
      async (log_file: string, cached?: boolean): Promise<LogDetails> => {
        const result = await get_log_details(log_file, cached);
        // Seed the per-log etag cache so the next `edit_log` for this
        // file sends `If-Match` and the first save races safely
        // against concurrent external edits. Local-filesystem logs and
        // (currently) JSON-format logs leave `result.etag` undefined,
        // in which case the cache stays empty and falls through to
        // last-writer-wins on the first save — matching the previous
        // behavior. S3-backed .eval files are the path that gets the
        // protection.
        if (result.etag) {
          editEtagByLog.set(log_file, result.etag);
        }
        return result;
      }
    ),
    get_log_info: middleware("get_log_info", (log_file: string) =>
      api.get_log_info(encodePathParts(log_file))
    ),
    get_log_sample: middleware("get_log_sample", get_log_sample),
    get_log_zip_access: middleware("get_log_zip_access", get_log_zip_access),
    open_log_file: middleware("open_log_file", (log_file, log_dir) => {
      return api.open_log_file(log_file, log_dir);
    }),
    download_file: middleware(
      "download_file",
      (
        download_file: string,
        file_contents:
          string | Blob | ArrayBuffer | ArrayBufferView<ArrayBuffer>
      ) => {
        return api.download_file(download_file, file_contents);
      }
    ),
    download_log: api.download_log
      ? middleware("download_log", (log_file: string) => {
          return api.download_log!(log_file);
        })
      : undefined,
    download_log_subset: api.download_log_subset
      ? middleware(
          "download_log_subset",
          (log_file: string, samples: SampleRef[], file_name: string) =>
            api.download_log_subset!(log_file, samples, file_name)
        )
      : undefined,
    save_log_subset: api.save_log_subset
      ? middleware(
          "save_log_subset",
          (log_file: string, samples: SampleRef[], file_name: string) =>
            api.save_log_subset!(log_file, samples, file_name)
        )
      : undefined,
    get_saved_views: api.get_saved_views
      ? middleware("get_saved_views", (folder: string) =>
          api.get_saved_views!(folder)
        )
      : undefined,
    put_saved_views: api.put_saved_views
      ? middleware(
          "put_saved_views",
          (folder: string, views: readonly object[]) =>
            api.put_saved_views!(folder, views)
        )
      : undefined,
    log_message: middleware(
      "log_message",
      (log_file: string, message: string) => {
        return api.log_message(log_file, message);
      }
    ),
    get_log_pending_samples: api.eval_pending_samples
      ? middleware("get_log_pending_samples", get_log_pending_samples)
      : undefined,
    get_log_sample_data: api.eval_log_sample_data
      ? middleware("get_log_sample_data", get_log_sample_data)
      : undefined,
    get_user_info: api.get_user_info
      ? middleware("get_user_info", () => api.get_user_info!())
      : undefined,
    get_app_config: middleware("get_app_config", () => api.get_app_config()),
    edit_log: api.edit_log
      ? middleware(
          "edit_log",
          async (
            log_file: string,
            update: LogUpdate,
            if_match_etag?: string
          ): Promise<EditLogResult> => {
            // Fall back to the etag returned by the previous successful
            // edit for this log if the caller didn't pass one. See the
            // `editEtagByLog` block above for why this exists.
            const effective = if_match_etag ?? editEtagByLog.get(log_file);
            const result = await api.edit_log!(log_file, update, effective);
            // Remember the new etag for the next call. A response
            // without an etag (e.g. local-filesystem edit) leaves the
            // cache as-is so a subsequent S3-backed edit on the same
            // path doesn't lose its `If-Match`.
            if (result.etag) {
              editEtagByLog.set(log_file, result.etag);
            }
            // The on-disk log just changed; drop both caches so the next
            // read (typically a `refreshLog` triggered by the dialog's
            // onSaved callback) re-fetches the new tags / log_updates.
            //   - `current_log`: JSON-format path in `get_log()` above.
            //   - `loadedEvalFile`: zip-backed reader used by .eval files
            //     via `remoteEvalFile()`. Without clearing this, the
            //     cached central-directory still points at the old
            //     header.json bytes.
            if (current_path === log_file) {
              current_log = undefined;
              current_path = undefined;
            }
            if (loadedEvalFile.file === log_file) {
              loadedEvalFile.file = undefined;
              loadedEvalFile.remoteLog = undefined;
            }
            return result;
          }
        )
      : undefined,
    list_searches: api.list_searches
      ? middleware("list_searches", (search_type, count) =>
          api.list_searches!(search_type, count)
        )
      : undefined,
    post_search: api.post_search
      ? middleware("post_search", (transcriptDir, transcriptId, request) =>
          api.post_search!(transcriptDir, transcriptId, request)
        )
      : undefined,
    get_search_result: api.get_search_result
      ? middleware(
          "get_search_result",
          (transcriptDir, transcriptId, search_id, scope) =>
            api.get_search_result!(
              transcriptDir,
              transcriptId,
              search_id,
              scope
            )
        )
      : undefined,
  };
};

// Top type for "any function" — every function is assignable to it, so it
// works as the generic constraint for the middleware plumbing.
type AnyFn = (...args: never[]) => unknown;

type Middleware<T extends AnyFn> = (
  name: string,
  fn: T,
  args: Parameters<T>,
  result: ReturnType<T>
) => ReturnType<T>;

const debugMiddleware: Middleware<AnyFn> = (name, _fn, args, result) => {
  if (result instanceof Promise) {
    const startTime = performance.now();
    return result.then((returned: unknown) => {
      const duration = performance.now() - startTime;
      console.log(`[ClientAPI] ${name}`, {
        args,
        returned,
        duration: `${duration.toFixed(2)}ms`,
      });
      return returned;
    });
  } else {
    console.log(`[ClientAPI] ${name}`, { args, returned: result });
    return result;
  }
};

const applyMiddleware = <T extends AnyFn>(
  name: string,
  fn: T,
  middlewares: Middleware<T>[]
): T => {
  if (middlewares.length === 0) return fn;

  /* eslint-disable @typescript-eslint/no-unsafe-type-assertion -- generic-signature boundary: TypeScript resolves `fn(...args)` against T's constraint (AnyFn, returning unknown) rather than T itself, and can't express "a wrapper with the same signature as T" for the return */
  const wrapped = (...args: Parameters<T>): ReturnType<T> => {
    let result = fn(...args) as ReturnType<T>;

    for (const middleware of middlewares) {
      result = middleware(name, fn, args, result);
    }

    return result;
  };
  return wrapped as T;
  /* eslint-enable @typescript-eslint/no-unsafe-type-assertion */
};

const createMiddlewareWrapper = (middlewares: Middleware<AnyFn>[]) => {
  return <T extends AnyFn>(name: string, fn: T): T => {
    // The middleware list is heterogeneous w.r.t. the wrapped function; the
    // mixed-variance Middleware<AnyFn> → Middleware<T> reshape is sound here.
    return applyMiddleware(name, fn, middlewares as Middleware<T>[]);
  };
};
